import { Activity, ScanLine, Bell, Hand, Brain, Users, Globe2, MapPin } from "lucide-react";

// =============================================================================
// Single source of truth for every figure used on the /market page.
// Sourced from WearSpray_Market_Opportunity_Emergent_Brief_2026.docx (29 Aug 2026).
// Update a number here and it flows to /market's stat cards, FAQ, sources,
// the homepage teaser, and the MarketPulseMap ticker, so a correction only
// has to happen in one place (this is how the earlier 38%/53% drift happened).
// Note: unrelated to `marketData.js`, which powers the separate adjacent-market
// convergence chart (MarketConvergence.js).
// =============================================================================
export const KEY_FIGURES = {
  aadForgetPct: "65%",
  aadReapplyFraction: "about one third",
  euRoutineUsePct: "62%",
  euOnceDailyPct: "34%",
  outdoorParticipants: "183.2M",
  outdoorParticipantsPct: "59%",
  usPop2544: "91.9M",
  euPop2544: "~131M",
  caribbeanPop2544: "~12.7M",
  combinedPop2544: "235.6 million",
  combinedPop2544Short: "~236M",
  coreAudience: "58–85 million",
  coreAudienceShort: "58–85M",
  caribbeanArrivals: "35M",
  caribbeanCruiseVisits: "35.5 million",
  aadReapplyShort: "~1/3",
};

// --- Part 2.2 · The problem, in numbers -------------------------------------
export const STATS = [
  {
    value: KEY_FIGURES.aadForgetPct,
    body: `of people surveyed said they often forget to reapply sunscreen, and only ${KEY_FIGURES.aadReapplyFraction} reapply every two hours while outside, the frequency U.S. FDA and dermatology guidance advises.`,
    source: "American Academy of Dermatology sun safety survey; U.S. FDA sunscreen guidance",
  },
  {
    value: KEY_FIGURES.euRoutineUsePct,
    body: `of Europeans use sunscreen routinely or often, yet ${KEY_FIGURES.euOnceDailyPct} of those who used it in the sun applied it only once all day.`,
    source: "17-country sun-exposure survey, SKIN Journal / EADV, sponsored by La Roche-Posay",
  },
  {
    value: KEY_FIGURES.outdoorParticipants,
    body: `Americans took part in outdoor recreation in 2025, ${KEY_FIGURES.outdoorParticipantsPct} of the population age six and older, the recurring occasion where reapplication most often breaks down.`,
    source: "Outdoor Industry Association, 2026 Outdoor Participation Trends Report",
  },
];

// --- Part 2.3 · Sense → Understand → Prompt → Act → Learn ------------------
export const LOOP = [
  { t: "Sense", Icon: Activity, c: "#00e5ff", d: "Reads the signals that matter, perspiration, skin temperature, activity, exposure." },
  { t: "Understand", Icon: ScanLine, c: "#3ad6a0", d: "Turns those signals into personal context." },
  { t: "Prompt", Icon: Bell, c: "#e6c068", d: "Tells you at the right moment, not on an arbitrary timer." },
  { t: "Act", Icon: Hand, c: "#ff2d78", d: "Puts the product on your wrist to act with. The step reminder apps stop short of." },
  { t: "Learn", Icon: Brain, c: "#b794f6", d: "Learns your rituals and routines over time." },
];

// --- Part 2.4b · Category momentum ("Why now") ------------------------------
export const MOMENTUM = [
  { v: "$2.7B", t: "in U.S. sun care sales for the 12 months ending March 2026, up 6%; prestige sun care reached $774.9M, up 11%.", source: "Circana" },
  { v: "+17%", t: "growth in European prestige sun care, reaching €239.7M, with facial SPF up 26% in the same period.", source: "Circana" },
  { v: "+11%", t: "growth in Latin American sun protection sales to $76M; ecommerce is nearly three quarters of sales, and Brazil's prestige segment reached $10M, 63% online.", source: "Circana" },
  { v: "21% / 10%", t: "online beauty sales growth in North America and Europe respectively, outpacing store sales.", source: "NIQ, State of Beauty 2025" },
  { v: "~4 in 10", t: "U.S. adults reported owning a smartwatch in 2026, evidence that wearable habits are already mainstream.", source: "Pew Research Center" },
];

// --- Part 2.4c · Who adopts first -------------------------------------------
export const PRIMARY_AUDIENCE = {
  t: "Active, skincare-engaged women ages 25–44",
  d: "The strongest intersection of routine skincare, sun awareness, ecommerce purchasing, travel, outdoor activity and openness to wearable accessories. This is the lead paid-media and creator audience, not an exclusionary product identity, WearSpray is a platform for a broad range of liquids, lotions and gels, for anyone.",
};

export const SECONDARY_AUDIENCES = [
  { t: "Active men, 25–44", need: "Sport, golf, fishing, running, cycling, boating", channels: "Outdoor creators, sport retail, golf & fishing channels" },
  { t: "Parents & caregivers", need: "Family days, pool, beach, parks, youth sports", channels: "Parent creators, family travel, resorts, sporting events" },
  { t: "Adults 45–64", need: "Travel, golf, gardening, boating, skin health", channels: "Travel, dermatology education, golf, cruise, premium retail" },
  { t: "Outdoor workers & staff", need: "Long exposure, interrupted routines", channels: "Employer pilots, hospitality, landscaping, field teams" },
  { t: "Beauty & skincare enthusiasts", need: "Daily SPF, body care, routine optimization", channels: "Beauty creators, ecommerce, specialty retail" },
];

// --- Part 2.9 · Expansion (evaluation only, not core TAM) -------------------
export const EXPANSION = [
  {
    t: "South America",
    body: "Approximately 130M people ages 25–44 on a directional UN population basis, led by Brazil's strong online sun-care momentum. A second-wave expansion market, not included in the headline core audience above.",
  },
  {
    t: "South Africa",
    body: "20.6M people ages 25–44 (2026), with high UV exposure concentrated around Western Cape tourism and beach, Gauteng premium urban beauty, and KwaZulu-Natal coastal hospitality. A focused pilot market under evaluation.",
  },
];

// --- Part 2.5 · Market at a glance ------------------------------------------
export const TABLE_ROWS = [
  { label: "Broad 25–44 demographic", us: KEY_FIGURES.usPop2544, eu: KEY_FIGURES.euPop2544, carib: `${KEY_FIGURES.caribbeanPop2544} residents` },
  { label: "Core WearSpray planning market", us: "25–35M", eu: "30–45M", carib: "3–5M residents" },
  { label: "Early practical beachhead", us: "3–8M", eu: "8–15M", carib: "0.5–1.5M residents" },
  { label: "Travel opportunity", us: "Domestic leisure & resort demand", eu: "Intra-Europe & destination travel", carib: `${KEY_FIGURES.caribbeanArrivals} annual stay-over arrivals (2025)` },
];

export const INTERPRETATION_NOTES = [
  { t: "Broad demographic", d: "All residents 25–44 in the geography. A population benchmark, not a forecast of purchasers." },
  { t: "Core planning market", d: "People with both product relevance (outdoor, sun-care, travel, skincare) and convenience friction. A planning range informed by population and behavior data, not a guarantee of conversion." },
  { t: "Early beachhead", d: "The concentrated subset practical to reach through selected digital, travel, resort, and specialty retail channels first." },
];

// --- Part 2.6 · Region snapshots ---------------------------------------------
export const REGIONS = [
  {
    Icon: MapPin,
    t: "United States",
    tag: "The proving ground",
    body: `${KEY_FIGURES.usPop2544} adults 25–44 (U.S. Census, 2025). With ${KEY_FIGURES.outdoorParticipantsPct} of Americans participating in outdoor recreation (Outdoor Industry Association, 2025), the outdoor-exposed pool in this cohort is roughly 54M. First markets: Florida, California, Texas, Hawaii, and the coastal Southeast, high-UV lifestyles, resort and watersport settings where the need demonstrates itself.`,
  },
  {
    Icon: Globe2,
    t: "Europe",
    tag: "Sun-aware and growing",
    body: `${KEY_FIGURES.euPop2544} adults 25–44 across the EU-27 and UK (Eurostat; UK ONS). Europe's six largest sun-care markets reached roughly €1.6B in retail sales, growing 11.6% year over year (Circana), with online beauty outpacing in-store (NielsenIQ). Priority countries: UK, Germany, France, Spain, Italy, Portugal, Greece, each with a distinct angle, from daily-SPF convenience to beach, boating, and resort protection.`,
  },
  {
    Icon: Users,
    t: "Caribbean",
    tag: "Where the need is constant",
    body: `${KEY_FIGURES.caribbeanPop2544} residents ages 25–44, year-round sun, marine activity, plus roughly ${KEY_FIGURES.caribbeanArrivals} international stay-over arrivals and ${KEY_FIGURES.caribbeanCruiseVisits} cruise visits in 2025 (Caribbean Tourism Organization), landing in exactly the high-sun, luggage-light, poolside context WearSpray is built for. Resort, marina, beach-club, and travel retail make the region a natural demonstration market.`,
  },
];

// --- Part 2.7 · FAQ ----------------------------------------------------------
export const FAQS = [
  {
    q: "Who is WearSpray designed for?",
    a: "Active, sun-aware adults roughly 25–44 who already use sunscreen and skincare, are comfortable with wearables, and want their trusted products accessible during activity, plus travelers who want protection without carrying bottles.",
  },
  {
    q: "How large is the addressable market?",
    a: `Across the U.S., Europe (EU-27 + UK), and the Caribbean: ${KEY_FIGURES.combinedPop2544Short} people ages 25–44 in total, of whom an estimated ${KEY_FIGURES.coreAudienceShort} are core, need-based potential consumers. These are planning estimates from official population and behavior data, not sales forecasts.`,
  },
  {
    q: "Why does sunscreen lead?",
    a: `Because reapplication is where efficacy lives and where behavior fails. The FDA advises reapplying at least every two hours; only ${KEY_FIGURES.aadReapplyFraction} of sunscreen users actually do while outside. A product worn on the body closes the gap between the intention and the act.`,
  },
  {
    q: "What does self-refillable mean?",
    a: "You fill WearSpray with a broad range of liquids, lotions and gels sold over-the-counter, sunscreen, lotion, repellent, sanitizer, that you already trust. Zinc-based sunscreens work too; just give the wrist band a quick shake first, the same way you'd give a bottle of sunscreen a quick shake before applying, so the formulation moves freely to the nozzle. It's a reusable accessory platform, not a proprietary-cartridge system.",
  },
  {
    q: "Is WearSpray a medical device?",
    a: "No. WearSpray is a convenience and behavior-support accessory. It doesn't diagnose or treat any condition, and it doesn't replace sun-safety judgment.",
  },
  {
    q: "What's the smart/sensor capability?",
    a: "A working prototype of the companion app exists today (protection countdown, UV and environmental intelligence, contextual reminders), built and continually refined by our software team ahead of a public release. A wearable sensor module, reading perspiration, skin temperature, and activity to personalize guidance, is in development.",
  },
  {
    q: "How can a brand or retailer partner with WearSpray?",
    a: "WearSpray is designed as a platform: the reusable wrist-worn format can complement existing sunscreen, skincare, and repellent brands by making their compatible products more accessible in beach, travel, outdoor, sport, resort, and daily-routine settings. Contact greg@wearspray.com.",
  },
];

export const FAQ_JSON_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const SOURCES = [
  "U.S. FDA, Sunscreen: How to Help Protect Your Skin from the Sun (reapplication guidance).",
  `American Academy of Dermatology, sun safety survey (${KEY_FIGURES.aadForgetPct} often forget to reapply; ${KEY_FIGURES.aadReapplyFraction} reapply every two hours).`,
  `U.S. Census Bureau, Vintage 2025 Population Estimates, ages 25–44 (${KEY_FIGURES.usPop2544}).`,
  `Eurostat & UK ONS, EU27 and UK population, ages 25–44 (${KEY_FIGURES.euPop2544} combined).`,
  "UN World Population Prospects (2024 revision), Caribbean, South America population and age cohorts.",
  `Caribbean Tourism Organization, 2025 international stay-over arrivals (${KEY_FIGURES.caribbeanArrivals}) and cruise visits (${KEY_FIGURES.caribbeanCruiseVisits}).`,
  "Circana, U.S., European and Latin American sun-care retail sales, 12 months ending March 2026.",
  `Outdoor Industry Association, 2026 Outdoor Participation Trends Report (${KEY_FIGURES.outdoorParticipants} U.S. participants, ${KEY_FIGURES.outdoorParticipantsPct}).`,
  "Pew Research Center, Americans and AI 2026 (smartwatch ownership).",
  "SKIN Journal / European Academy of Dermatology and Venereology, sponsored by La Roche-Posay, 17-country sun-exposure survey.",
  "Statistics South Africa, mid-year 2026 population estimates, ages 25–44 (20.6M).",
  "NIQ, State of Beauty 2025 (ecommerce growth in beauty and personal care).",
].join(" ");
